import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { makeStyles } from 'tss-react/mui';
import Button from '@mui/material/Button';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Helmet from 'react-helmet';
import { useIntl } from 'ha/i18n';
import { Grid, units } from '@hbf/dsl';
import { checkSearchQueryEmpty } from 'ha/utils/checkSearchQueryEmpty';

import { PagerProps } from './types';
import { PagerButton } from './PagerButton';

const useStyles = makeStyles()({
  indentTop: {
    paddingTop: units.rem(0.5),
  },
  indentBottom: {
    paddingBottom: units.rem(0.5),
  },
});

export const Pager: React.FC<PagerProps> = ({
  pager,
  pagerData: {
    firstPage,
    lastPage,
    prevPage,
    nextPage,
    visiblePages,
    headCollapsed,
    tailCollapsed,
  },
  withHeadLinks = true,
  indentTop = true,
  onPageChange,
  variant = 'outlined',
}) => {
  const { classes, cx } = useStyles();
  const { urlResolver } = useIntl();
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();

  const getPageHref = React.useCallback(
    (pageNum: number, { isHeadLink = false } = {}) => {
      const url = new URL(urlResolver.getBaseUrl());

      url.pathname = pathname;

      if (!isHeadLink) {
        url.search = search;

        url.searchParams.delete('page');
      }

      if (pageNum !== 1) {
        url.searchParams.set('page', String(pageNum));
      }

      return url.href;
    },
    [pathname, search, urlResolver],
  );

  if (!pager.quantity || pager.total <= pager.quantity) {
    return null;
  }
  const isQueryEmpty = checkSearchQueryEmpty(searchParams);

  return (
    <Grid
      container
      justifyContent="flex-end"
      data-test-locator="Pagination Row"
      className={cx(classes.indentBottom, [{ [classes.indentTop]: indentTop }])}
    >
      {prevPage && (
        <PagerButton
          page={prevPage}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
          variant={variant}
        >
          <ChevronLeft />
        </PagerButton>
      )}

      {prevPage && withHeadLinks && isQueryEmpty && (
        <Helmet>
          <link
            key="link prev"
            rel="prev"
            href={getPageHref(prevPage.num, {
              isHeadLink: true,
            })}
          />
        </Helmet>
      )}

      {headCollapsed && (
        <PagerButton
          page={firstPage}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
          variant={variant}
        />
      )}

      {headCollapsed && <Button disabled>...</Button>}

      {visiblePages.map(page => (
        <PagerButton
          page={page}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
          data-test-locator={`Page Button ${page.num}`}
          key={page.num}
          variant={variant}
        />
      ))}

      {tailCollapsed && <Button disabled>...</Button>}

      {tailCollapsed && (
        <PagerButton
          page={lastPage}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
          data-test-locator="Last Page Button"
          variant={variant}
        />
      )}

      {nextPage && (
        <PagerButton
          page={nextPage}
          getPageHref={getPageHref}
          onPageChange={onPageChange}
          data-test-locator="Pager Next Page"
          variant={variant}
        >
          <ChevronRight />
        </PagerButton>
      )}

      {nextPage && withHeadLinks && isQueryEmpty && (
        <Helmet>
          <link
            key="link next"
            rel="next"
            href={getPageHref(nextPage.num, {
              isHeadLink: true,
            })}
          />
        </Helmet>
      )}
    </Grid>
  );
};
